import { Component, ElementRef, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  VIAJES_FIRMA_EMAIL_PATTERN,
  isCommentRequiredFirmaTipo,
} from 'src/app/services/firma-digital/viajes-firma.mapper';
import { ViajesFirmaTipo } from 'src/core/apis/viajes.firma';

export interface HormigonFirmaDatosModalData {
  tipoFirma: ViajesFirmaTipo;
  rut?: string;
  dv?: string;
  name?: string;
  email?: string;
  comentario?: string;
  m3Returned?: number | null;
}

export interface HormigonFirmaDatosModalResult {
  rut: string;
  dv: string;
  name: string;
  email: string;
  comentario: string;
  m3Returned?: number;
}

type FirmaDatosField = 'm3Returned' | 'rut' | 'dv' | 'name' | 'email' | 'comentario';
type FirmaDatosErrors = Partial<Record<FirmaDatosField, string>>;

const FIELD_LABELS: Record<FirmaDatosField, string> = {
  m3Returned: 'Cantidad retornada',
  rut: 'RUT',
  dv: 'Dígito verificador',
  name: 'Nombre',
  email: 'Correo',
  comentario: 'Comentarios',
};

@Component({
  selector: 'app-hormigon-firma-datos-modal',
  templateUrl: './hormigon-firma-datos-modal.component.html',
  styleUrls: [
    './hormigon-firma-digital-page.component.css',
    './hormigon-firma-datos-modal.component.css',
  ],
  standalone: false,
})
export class HormigonFirmaDatosModalComponent {
  rut = '';
  dv = '';
  name = '';
  email = '';
  comentario = '';
  m3Returned: number | null = null;
  error = '';
  errors: FirmaDatosErrors = {};
  private submitted = false;

  readonly emailPattern = VIAJES_FIRMA_EMAIL_PATTERN;
  readonly commentRequired: boolean;
  readonly showEmail: boolean;
  readonly askReturnQuantity: boolean;

  constructor(
    private readonly dialogRef: MatDialogRef<
      HormigonFirmaDatosModalComponent,
      HormigonFirmaDatosModalResult
    >,
    @Inject(MAT_DIALOG_DATA) readonly data: HormigonFirmaDatosModalData,
    private readonly host: ElementRef<HTMLElement>
  ) {
    this.rut = data.rut ?? '';
    this.dv = data.dv ?? '';
    this.name = data.name ?? '';
    this.email = data.email ?? '';
    this.comentario = data.comentario ?? '';
    this.m3Returned = data.m3Returned ?? null;
    this.commentRequired = isCommentRequiredFirmaTipo(data.tipoFirma);
    this.showEmail = data.tipoFirma === 'RECEPCION';
    this.askReturnQuantity = data.tipoFirma === 'DEVOLUCION_HORMIGON';
  }

  onDvChange(value: string): void {
    this.dv = (value ?? '').replace(/[^0-9kK]/g, '').slice(0, 1).toUpperCase();
    this.onFieldChange();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  onFieldChange(): void {
    if (this.submitted) {
      this.applyValidation();
    }
  }

  continue(): void {
    this.submitted = true;
    if (!this.applyValidation()) {
      setTimeout(() =>
        this.host.nativeElement
          .querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.focus()
      );
      return;
    }
    const comentario = this.comentario.trim();
    const m3 = Number(this.m3Returned);
    const mail = this.showEmail ? this.email.trim() : '';
    this.dialogRef.close({
      rut: this.rut.trim(),
      dv: this.dv.trim().toUpperCase(),
      name: this.name.trim(),
      email: mail,
      comentario,
      m3Returned: this.askReturnQuantity ? m3 : undefined,
    });
  }

  private applyValidation(): boolean {
    this.errors = this.validate();
    const invalid = (Object.keys(this.errors) as FirmaDatosField[]).map(
      (field) => FIELD_LABELS[field]
    );
    this.error = invalid.length
      ? `Revisa los campos marcados: ${invalid.join(', ')}.`
      : '';
    return invalid.length === 0;
  }

  private validate(): FirmaDatosErrors {
    const errors: FirmaDatosErrors = {};
    const rut = this.rut.trim();
    const dv = this.dv.trim();

    if (this.askReturnQuantity && !(Number(this.m3Returned) > 0)) {
      errors.m3Returned = 'Ingresa una cantidad mayor a 0.';
    }

    if (!rut) {
      errors.rut = 'Ingresa el RUT.';
    }
    if (!dv) {
      errors.dv = 'Ingresa el dígito verificador.';
    }
    if (!errors.rut && !errors.dv) {
      const rutLen = rut.length + dv.length;
      if (!(rutLen === 8 || rutLen === 9)) {
        errors.rut = 'Ingresa un RUT válido.';
      }
    }

    if (!this.name.trim()) {
      errors.name = 'Ingresa el nombre.';
    }

    const mail = this.showEmail ? this.email.trim() : '';
    if (mail && !this.emailPattern.test(mail)) {
      errors.email = 'El correo ingresado no es válido.';
    }

    if (this.commentRequired && !this.comentario.trim()) {
      errors.comentario = 'Ingresa un comentario.';
    }

    return errors;
  }
}
